import { useTranslation } from '../../context/TranslationContext';
import { formatPrice } from '../../utils/formatPrice';
import { formatWeight } from '../../utils/formatWeight';
import { localizedField } from '../../utils/localizedField';
import { getShippingDominantRate } from '../../utils/shippingVat';
import PriceBreakdown from '../PriceBreakdown';

export default function OrderSummarySection({
  items, summary, billingStatus, volumeDiscount, appliedBalance,
  couponCode, setCouponCode, couponFeedbackKey, onApplyCoupon,
  appliedCouponCodes = [],
  isApplyingCoupon = false,
  isPickup = false,
}) {
  const { t } = useTranslation();
  const couponIsApplied = couponFeedbackKey === 'couponApplied';

  return (
    <>
      {/* Items */}
      <div className="items-summary">
        {items?.length > 0 ? items.map((item, idx) => {
          const safeNumber = (v) => {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : null;
          };
          const itemPrice = safeNumber(item.price); // NET (checkout items are net)
          const origPriceNet = item.has_special_price ? safeNumber(item.original_price || item.originalPrice) : null;
          // Business shows NET directly; retail grosses the per-unit NET up by the
          // line VAT and rounds to centimes — the SHOWN unit price the customer
          // sees on the cart/product page (net 4.82 @ 2.6% → shown 4.95).
          const toGross = (net) => (net != null && item.vat_rate > 0 ? Math.round(net * (1 + item.vat_rate / 100) * 100) / 100 : net);
          const displayPrice = summary.vatExclusive ? itemPrice : toGross(itemPrice);
          const displayOrig = origPriceNet != null ? (summary.vatExclusive ? origPriceNet : toGross(origPriceNet)) : null;
          const qty = Number(item.quantity) || 0;
          const priceTotal = displayPrice != null ? displayPrice * qty : null;
          const originalPriceTotal = displayOrig != null ? displayOrig * qty : null;
          return (
            <div key={idx} className="checkout-item">
              {item.image ? <img src={item.image} alt={localizedField(item, 'name', t.langCode)} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} /> : null}
              <div>
                <div><strong>{localizedField(item, 'name', t.langCode)} - {item.weight_value != null ? formatWeight(item.weight_value, item.weight_unit) : null}</strong></div>
                <div>{t('quantity', 'Quantity')}: {qty}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {priceTotal != null && (
                    <span style={{ color: item.has_special_price ? '#c92a2a' : undefined, fontWeight: item.has_special_price ? 700 : undefined }}>
                      {formatPrice(priceTotal)}
                    </span>
                  )}
                  {originalPriceTotal != null && (
                    <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em' }}>
                      {formatPrice(originalPriceTotal)}
                    </span>
                  )}
                  {item.discount_percentage != null && (
                    <span style={{ fontSize: '0.75em', color: '#2f9e44', fontWeight: 600, background: '#ebfbee', padding: '1px 6px', borderRadius: 4 }}>
                      {t('youSave', 'You save:')} {item.discount_percentage}%
                    </span>
                  )}
                </div>
                {item.vat_rate ? <div style={{ color: '#888', fontSize: '0.85em' }}>{summary.vatExclusive ? t('excl', 'excl.') : t('incl', 'incl.')} {item.vat_rate}% {t('vat', 'VAT')}</div> : null}
                {qty > Number(item.stock_quantity || 0) && (
                  <div role="status" style={{ color: '#d9480f', marginTop: 4 }}>
                    <div style={{ fontSize: '0.8em', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>
                      {t('stockShortfallTitle', 'Guaranteed Fresh & Crafted for You')}
                    </div>
                    <div style={{ fontSize: '0.85em', fontWeight: 600 }}>
                      {t('stockShortfallNote', 'We make everything in small, exclusive batches to ensure the highest quality. Your order is secured for our next production run and will be shipped right after. Thank you for choosing fresh!')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }) : (
          <p role="status" aria-live="polite">{t('loadingItems', 'Loading items…')}</p>
        )}
      </div>

      {/* Free Items */}
      {summary.freeItems && summary.freeItems.length > 0 && (
        <div style={{ background: '#f0fdf4', padding: '0.75rem', borderRadius: 8, marginBottom: 12 }}>
          {summary.freeItems.map((fi, i) => (
            <div key={i} style={{ color: '#16a34a', fontWeight: 600, fontSize: '0.875rem' }}>
              {fi.reasonKey ? t(fi.reasonKey, '', fi.reasonParams) : fi.reason} {t('applied', 'Applied')}
            </div>
          ))}
        </div>
      )}

      {/* Price Breakdown */}
      <div className="order-breakdown">
        {(() => {
          const isRetail = !summary.vatExclusive;
          const dominantRate = getShippingDominantRate((items || []).map(i => ({
            net: (Number(i.price) || 0) * (Number(i.quantity) || 1),
            vat_rate: Number(i.vat_rate) || 0,
          })));
          const gf = isRetail && dominantRate > 0 ? 1 + dominantRate / 100 : 1;
          const gross = (net) => Math.round((Number(net) || 0) * gf * 100) / 100;
          return (
            <PriceBreakdown
              t={t}
              couponDiscount={isRetail ? gross(summary.couponDiscount) : summary.couponDiscount}
              ruleDiscount={isRetail ? gross(summary.ruleDiscount) : summary.ruleDiscount}
              shippingCost={isRetail ? gross(summary.shipping) : summary.shipping}
              shippingFree={summary.shippingFree}
              isPickup={isPickup}
              volumeDiscount={isRetail ? gross(volumeDiscount) : volumeDiscount}
              volumeDiscountPercentage={billingStatus?.discountPercentage ?? null}
              appliedBalance={appliedBalance}
              roundOffDiff={summary.roundOffDiff}
              vatExclusive={summary.vatExclusive}
              netSubtotal={summary.netSubtotal}
              netShipping={isRetail ? 0 : summary.netShipping}
              vatOnDiscount={summary.vatOnDiscount}
              vatAmount={isRetail ? undefined : summary.vat}
            />
          );
        })()}
        <div className="order-total">
          <span>
            {Math.round(summary.roundOffDiff * 100) !== 0 ? `${t('totalPayable')} *` : t('total')}:
          </span>
          <span>{formatPrice(Math.round(summary.roundOffDiff * 100) !== 0 ? summary.roundedTotal : summary.total)}</span>
        </div>
        <div style={{ color: '#666', fontSize: '0.85em' }}>
          {!summary.vatExclusive && (
            <>
              <span>** {t('vat', 'VAT')}:</span>
              <span>{formatPrice(summary.vat)}</span>
            </>
          )}
        </div>
      </div>

      {/* Coupon */}
      <div className="coupon-section">
        <label htmlFor="checkout-coupon-code" className="sr-only">
          {t('couponCode', 'Coupon code')}
        </label>
        <input
          id="checkout-coupon-code"
          type="text"
          value={couponCode}
          onChange={e => setCouponCode(e.target.value)}
          placeholder={t('enterCoupon', couponIsApplied ? 'Enter another code' : 'Enter coupon code…')}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          onClick={onApplyCoupon}
          disabled={isApplyingCoupon || !couponCode}
          aria-label={t('applyCoupon', 'Apply coupon')}
        >
          {t('applyCoupon', 'Apply')}
        </button>
      </div>
      {couponFeedbackKey && (
        <div
          className="coupon-feedback"
          style={{ color: couponFeedbackKey === 'couponApplied' ? 'green' : 'red' }}
          role={couponFeedbackKey === 'couponApplied' ? 'status' : 'alert'}
          aria-live="polite"
        >
          {t(couponFeedbackKey)}
        </div>
      )}

      {Math.round(summary.roundOffDiff * 100) !== 0 && (
        <div style={{ color: '#888', fontSize: '0.85em', marginBottom: '12px' }}>
          * {billingStatus?.monthlyEnabled ? t('roundingNoteMonthly') : t('roundingNote')}
        </div>
      )}
    </>
  );
}
